import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiSearch, FiPlus, FiBriefcase, FiTrash2, FiCheck } from 'react-icons/fi';

interface InquirySourceModalProps {
  sources: string[];
  selectedSource: string;
  onSelectSource: (source: string) => void;
  onAddSource?: (newSource: string) => void;
  onDeleteSource?: (sourceToDelete: string) => void;
  onClose: () => void;
}

export const InquirySourceModal: React.FC<InquirySourceModalProps> = ({
  sources,
  selectedSource,
  onSelectSource,
  onAddSource,
  onDeleteSource,
  onClose,
}) => {
  const [sourceList, setSourceList] = useState<string[]>(sources);
  const [searchInput, setSearchInput] = useState('');

  const handleAdd = () => {
    const trimmed = searchInput.trim();
    if (!trimmed) return;
    if (!sourceList.includes(trimmed)) {
      const updated = [...sourceList, trimmed];
      setSourceList(updated);
      onAddSource?.(trimmed);
    }
    setSearchInput('');
  };

  const handleDelete = (source: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = sourceList.filter((s) => s !== source);
    setSourceList(updated);
    onDeleteSource?.(source);
  };

  const filteredSources = sourceList.filter((s) =>
    s.toLowerCase().includes(searchInput.toLowerCase()),
  );

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -6 }}
      transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
      className="absolute top-full mt-2.5 z-40 w-[260px] -right-12 sm:right-0 sm:w-[320px] rounded-3xl border border-[#3A3A3C] bg-[#1C1C1E] p-3.5 shadow-[0_20px_50px_rgba(0,0,0,0.85)] backdrop-blur-xl"
    >
      {/* Search or Add input row */}
      <div className="relative mb-3 flex items-center gap-2">
        <div className="relative flex-1">
          <FiSearch
            size={16}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <input
            type="text"
            placeholder="Search or add a source"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
            }}
            className="w-full rounded-2xl border border-[#3A3A3C] bg-[#2A2A2D] py-2.5 pl-10 pr-3 text-xs text-zinc-100 placeholder:text-zinc-500 outline-none focus:border-[#48484A]"
          />
        </div>
        <button
          onClick={handleAdd}
          className="inline-flex items-center gap-1 rounded-2xl border border-[#3A3A3C] bg-[#2C2C2E] px-3 py-2.5 text-xs font-semibold text-white transition hover:bg-[#3A3A3C] shrink-0"
        >
          <FiPlus size={14} />
          Add
        </button>
      </div>

      {/* Sources list with no-scrollbar */}
      <div className="max-h-60 space-y-1.5 overflow-y-auto no-scrollbar pr-1">
        <button
          onClick={() => {
            onSelectSource('All');
            onClose();
          }}
          className={`flex w-full items-center justify-between rounded-2xl border border-[#2C2C2E] p-3 text-left transition ${
            selectedSource === 'All'
              ? 'bg-[#3A3A3C] border-[#48484A]'
              : 'bg-[#2A2A2D] hover:bg-[#3A3A3C]'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-zinc-800 text-zinc-300">
              <FiBriefcase size={15} />
            </div>
            <span className="text-sm font-medium text-white">All Sources</span>
          </div>
          {selectedSource === 'All' && (
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#00D45A] text-black">
              <FiCheck size={14} className="stroke-[3]" />
            </div>
          )}
        </button>

        {filteredSources.map((source) => {
          const isSelected = selectedSource === source;
          return (
            <div
              key={source}
              onClick={() => {
                onSelectSource(source);
                onClose();
              }}
              className={`flex cursor-pointer items-center justify-between rounded-2xl border border-[#2C2C2E] p-3 text-left transition ${
                isSelected ? 'bg-[#3A3A3C] border-[#48484A]' : 'bg-[#2A2A2D] hover:bg-[#3A3A3C]'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-zinc-800 text-zinc-300">
                  <FiBriefcase size={15} />
                </div>
                <span className="text-sm font-medium text-white">{source}</span>
              </div>
              <button
                onClick={(e) => handleDelete(source, e)}
                className="flex h-7 w-7 items-center justify-center rounded-xl text-zinc-500 transition hover:bg-red-500/20 hover:text-red-400"
                title="Delete source"
              >
                <FiTrash2 size={15} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Save Changes button */}
      <div className="mt-3 border-t border-[#2C2C2E] pt-3">
        <button
          onClick={onClose}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-zinc-700 py-2.5 text-xs font-semibold text-white transition hover:bg-zinc-600"
        >
          <FiCheck size={16} />
          Save Changes
        </button>
      </div>
    </motion.div>
  );
};
